import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import { SERVICE_VARIANT_TYPE } from '@/lib/service-variant-types'
import type { ApiResponse } from '@/types'
import type { OrgTreeServiceVariant } from '@/services/org-tree'

type TenantRecord = Record<string, unknown>

export interface TenantInstance {
  id: string | number
  instanceId?: string | null
  serviceId?: string | null
  tenantId?: string | number | null
  branchId?: string | number | null
  parentInstanceId?: string | null
  name: string
  identifier?: string | null
  status?: string | number | null
  serviceVariant?: string | number | null
  serviceVariantName?: string | null
  primaryNode?: string | number | null
  accessorNode?: string | number | null
  countryId?: string | number | null
  countryName?: string | null
  countryCode?: string | null
  timeZoneId?: string | number | null
  timeZone?: string | null
  currencyCode?: string | null
  currencySymbol?: string | null
  createdOn?: string | null
  createdByUserId?: string | number | null
  createdByName?: string | null
  ownerUserId?: string | number | null
  ownerName?: string | null
  ownerEmail?: string | null
  ownerPhone?: string | null
  superAdminUserId?: string | number | null
  superAdminName?: string | null
  superAdminEmail?: string | null
  superAdminPhone?: string | null
  logo?: string | null
  levelVariantName?: string | null
  levelVariantTierCode?: string | null
}

export interface TenantGeneratedCredentials {
  userId?: string | number | null
  username?: string | null
  email?: string | null
  phone?: string | null
  name?: string | null
  password?: string | null
}

export interface TenantSuperAdminProvisionResult {
  tenant: TenantInstance | null
  credentials: TenantGeneratedCredentials | null
}

type TenantServiceVariantRecord = Record<string, unknown>

const getValue = (record: TenantRecord, ...keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }

  return undefined
}

const normalizeTenant = (record: TenantRecord): TenantInstance => ({
  id: getValue(record, 'id', 'instance_id') as string | number,
  instanceId: getValue(record, 'instance_id') as string | undefined,
  serviceId: getValue(record, 'service_id', 'instance_id') as string | undefined,
  tenantId: getValue(record, 'tenant_id') as string | number | undefined,
  branchId: getValue(record, 'branch_id') as string | number | undefined,
  parentInstanceId: getValue(record, 'parent_instance_id') as string | undefined,
  name: String(getValue(record, 'name', 'instance_id') ?? 'Unnamed tenant'),
  identifier: getValue(record, 'identifier', 'url_identifier') as string | undefined,
  status: getValue(record, 'status') as string | number | undefined,
  serviceVariant: getValue(record, 'service_variant') as string | number | undefined,
  serviceVariantName: getValue(record, 'service_variant_name') as string | undefined,
  primaryNode: getValue(record, 'primary_node') as string | number | undefined,
  accessorNode: getValue(record, 'accessor_node') as string | number | undefined,
  countryId: getValue(record, 'country_id', 'country') as string | number | undefined,
  countryName: getValue(record, 'country_name') as string | undefined,
  countryCode: getValue(record, 'country_code') as string | undefined,
  timeZoneId: getValue(record, 'time_zone_id') as string | number | undefined,
  timeZone: getValue(record, 'time_zone', 'timezone') as string | undefined,
  currencyCode: getValue(record, 'currency_code') as string | undefined,
  currencySymbol: getValue(record, 'currency_symbol') as string | undefined,
  createdOn: getValue(record, 'created_on') as string | undefined,
  createdByUserId: getValue(record, 'created_by_user_id') as string | number | undefined,
  createdByName: getValue(record, 'created_by_name') as string | undefined,
  ownerUserId: getValue(record, 'owner_user_id') as string | number | undefined,
  ownerName: getValue(record, 'owner_name') as string | undefined,
  ownerEmail: getValue(record, 'owner_email') as string | undefined,
  ownerPhone: getValue(record, 'owner_phone') as string | undefined,
  superAdminUserId: getValue(record, 'super_admin_user_id') as string | number | undefined,
  superAdminName: getValue(record, 'super_admin_name') as string | undefined,
  superAdminEmail: getValue(record, 'super_admin_email') as string | undefined,
  superAdminPhone: getValue(record, 'super_admin_phone') as string | undefined,
  logo: getValue(record, 'logo') as string | undefined,
  levelVariantName: getValue(record, 'level_variant_name') as string | undefined,
  levelVariantTierCode: getValue(record, 'level_variant_tier_code') as string | undefined,
})

const normalizeGeneratedCredentials = (record: TenantRecord): TenantGeneratedCredentials | null => {
  const credentials = record.credentials
  if (!credentials || typeof credentials !== 'object') {
    return null
  }

  const entry = credentials as TenantRecord
  return {
    userId: getValue(entry, 'user_id') as string | number | undefined,
    username: getValue(entry, 'username') as string | undefined,
    email: getValue(entry, 'email') as string | undefined,
    phone: getValue(entry, 'phone') as string | undefined,
    name: getValue(entry, 'name') as string | undefined,
    password: getValue(entry, 'password') as string | undefined,
  }
}

const normalizeTenantServiceVariant = (record: TenantServiceVariantRecord): OrgTreeServiceVariant => ({
  id: (getValue(record, 'service_variant', 'id') as string | number | undefined) ?? '',
  name: String(getValue(record, 'value', 'name', 'service_name', 'service_variant') ?? 'Variant'),
  value: getValue(record, 'value', 'name') as string | undefined,
  serviceId: getValue(record, 'service_id') as string | number | undefined,
  serviceUniqueId: getValue(record, 'service_variant_unique_id', 'service_unique_id') as string | number | undefined,
})

const getListPayload = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as Record<string, unknown>
  if (Array.isArray(record.data)) {
    return record.data as T[]
  }

  if (record.data && typeof record.data === 'object') {
    const nested = record.data as Record<string, unknown>
    if (Array.isArray(nested.results)) {
      return nested.results as T[]
    }
    if (Array.isArray(nested.list)) {
      return nested.list as T[]
    }
  }

  if (Array.isArray(record.results)) {
    return record.results as T[]
  }

  if (Array.isArray(record.list)) {
    return record.list as T[]
  }

  return []
}

const getTenantKey = (tenant: TenantInstance) => {
  const instanceId = String(tenant.instanceId ?? '').trim().toLowerCase()
  if (instanceId) {
    return `instance:${instanceId}`
  }

  const identifier = String(tenant.identifier ?? '').trim().toLowerCase()
  if (identifier) {
    return `identifier:${identifier}`
  }

  return `id:${String(tenant.id ?? '').trim().toLowerCase()}`
}

export const tenantsApi = {
  list: async (options?: {
    serviceInstanceId?: string | number | null
    uniqueLevelVariantId?: string | null
  }): Promise<TenantInstance[]> => {
    const serviceInstanceId = String(options?.serviceInstanceId ?? '').trim()
    const uniqueLevelVariantId = String(options?.uniqueLevelVariantId ?? '').trim()

    const tenantListResponse = await api.get<ApiResponse<TenantRecord[]> | TenantRecord[]>(
      API_PATHS.organisation.tenants.list,
      {
        params: serviceInstanceId
          ? {
            service_instance_id: serviceInstanceId,
            service_variant_type: SERVICE_VARIANT_TYPE.TENANT,
            unique_level_variant_id: uniqueLevelVariantId || undefined,
          }
          : undefined,
      },
    )

    const tenantRows = getListPayload<TenantRecord>(tenantListResponse.data)
      .map((record) => normalizeTenant(record))

    const merged = new Map<string, TenantInstance>()

    tenantRows.forEach((tenant) => {
      merged.set(getTenantKey(tenant), tenant)
    })

    const rows = Array.from(merged.values())
    if (rows.length === 0) {
      return []
    }

    return rows
  },

  archive: async (tenantId: string | number): Promise<TenantInstance | null> => {
    const response = await api.post<ApiResponse<TenantRecord> | TenantRecord>(
      API_PATHS.organisation.tenants.archive(tenantId),
      {},
    )
    const payload = Array.isArray(response.data)
      ? response.data[0]
      : 'data' in response.data
        ? response.data.data
        : response.data
    return payload ? normalizeTenant(payload) : null
  },

  restore: async (tenantId: string | number): Promise<TenantInstance | null> => {
    const response = await api.post<ApiResponse<TenantRecord> | TenantRecord>(
      API_PATHS.organisation.tenants.restore(tenantId),
      {},
    )
    const payload = Array.isArray(response.data)
      ? response.data[0]
      : 'data' in response.data
        ? response.data.data
        : response.data
    return payload ? normalizeTenant(payload) : null
  },

  updateSuperAdmin: async (tenantId: string | number, userId: string | number): Promise<TenantInstance | null> => {
    const response = await api.post<ApiResponse<TenantRecord> | TenantRecord>(
      API_PATHS.organisation.tenants.updateSuperAdmin(tenantId),
      { user_id: userId },
    )
    const payload = Array.isArray(response.data)
      ? response.data[0]
      : 'data' in response.data
        ? response.data.data
        : response.data
    return payload ? normalizeTenant(payload) : null
  },

  bootstrapSuperAdminFromOwner: async (tenantId: string | number): Promise<TenantSuperAdminProvisionResult> => {
    const response = await api.post<ApiResponse<TenantRecord> | TenantRecord>(
      API_PATHS.organisation.tenants.bootstrapSuperAdmin(tenantId),
      {},
    )
    const payload = Array.isArray(response.data)
      ? response.data[0]
      : 'data' in response.data
        ? response.data.data
        : response.data

    if (!payload) {
      return { tenant: null, credentials: null }
    }

    return {
      tenant: normalizeTenant(payload),
      credentials: normalizeGeneratedCredentials(payload),
    }
  },

  assignSupportSuperAdmin: async (tenantId: string | number, roleId: string): Promise<TenantSuperAdminProvisionResult> => {
    const response = await api.post<ApiResponse<TenantRecord> | TenantRecord>(
      API_PATHS.organisation.tenants.supportSuperAdmin(tenantId),
      { role: roleId },
    )
    const payload = Array.isArray(response.data)
      ? response.data[0]
      : 'data' in response.data
        ? response.data.data
        : response.data

    if (!payload) {
      return { tenant: null, credentials: null }
    }

    return {
      tenant: normalizeTenant(payload),
      credentials: normalizeGeneratedCredentials(payload),
    }
  },

  listServices: async (tenantInstanceId: string | number): Promise<OrgTreeServiceVariant[]> => {
    const response = await api.get<ApiResponse<TenantServiceVariantRecord[]> | TenantServiceVariantRecord[]>(
      API_PATHS.orgTree.instanceVariants.list,
      {
        params: {
          org_instance: tenantInstanceId,
        },
      },
    )
    const payload = Array.isArray(response.data) ? response.data : response.data.data
    return Array.isArray(payload) ? payload.map((record) => normalizeTenantServiceVariant(record)) : []
  },

  updateServices: async (tenantInstanceId: string | number, serviceVariantIds: Array<string | number>) => {
    const response = await api.post(API_PATHS.orgTree.instanceVariants.add, {
      org_instance: tenantInstanceId,
      service_variant: serviceVariantIds,
    })

    return response.data
  },
}